import { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { RotateCcw, Save, Sparkles, Target } from "lucide-react";
import { useTran } from "@/hooks";
import { PageHeader, SearchableSelect, AppDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { showToast } from "@/lib/utils/toast";
import { useKpiTargetsStore, useCarbonTargetsStore } from "../hooks";
import CarbonInventoryTarget from "../components/CarbonInventoryTarget";
import FrameworkTargetsSection from "../components/FrameworkTargetsSection";

const TargetSettingsPage = observer(() => {
  const { t } = useTran();
  const {
    activeCategory,
    clearAllTargets,
    hasChangedTargets,
    resettingTargets,
    saveTargetsToApi,
    seedTargets,
    selectedFramework,
    selectedYear,
  } = useKpiTargetsStore();
  const { saveCarbonTargets, setCarbonValue } = useCarbonTargetsStore();
  const [activeScope, setActiveScope] = useState("frameworks");
  const [resetOpen, setResetOpen] = useState(false);

  const scopeOptions = useMemo(
    () => [
      { value: "frameworks", label: t("Frameworks") },
      { value: "carbon", label: t("Carbon Inventory") },
    ],
    [t],
  );

  const handleSave = async () => {
    if (activeScope === "carbon") {
      const result = await saveCarbonTargets(selectedYear);
      if (result.success) {
        showToast.success(t("Carbon targets saved."));
      } else {
        showToast.error(result.error || t("Failed to save carbon targets."));
      }
      return;
    }

    try {
      const result = await saveTargetsToApi();
      const totalProcessed =
        result?.totalProcessed ??
        result?.data?.totalProcessed ??
        result?.total ??
        0;
      showToast.success(
        totalProcessed
          ? t("Saved {{count}} targets.", { count: totalProcessed })
          : t("Targets saved."),
      );
    } catch (error) {
      showToast.error(error?.message || t("Failed to save targets."));
    }
  };

  const handleSeedDemo = async () => {
    if (activeScope === "carbon") {
      setCarbonValue("scope1", "stationaryCombustion", "1250");
      setCarbonValue("scope1", "mobileCombustion", "780");
      setCarbonValue("scope1", "fugitiveEmissions", "24");
      setCarbonValue("scope1", "processEmissions", "160");
      setCarbonValue("scope2", "purchasedElectricity", "145000");
      setCarbonValue("scope2", "purchasedSteam", "38000");
      setCarbonValue("scope2", "purchasedCooling", "26000");
      const result = await saveCarbonTargets(selectedYear);
      if (result.success) {
        showToast.success(t("Carbon demo targets seeded."));
      } else {
        showToast.error(result.error || t("Failed to seed demo data."));
      }
      return;
    }

    try {
      const seeded = await seedTargets({
        year: selectedYear,
        framework: selectedFramework,
        category: activeCategory,
      });
      const total = seeded?.total || seeded?.created || 0;
      showToast.success(
        total
          ? t("Demo data created ({{count}} targets).", { count: total })
          : t("Demo data created."),
      );
    } catch (error) {
      showToast.error(error?.message || t("Failed to seed demo data."));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <PageHeader
          title={t("Global KPI Target Settings")}
          description={t(
            "Set global targets for quantitative disclosures. Applies to all reports.",
          )}
          icon={Target}
        />
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[180px]">
            <SearchableSelect
              value={activeScope}
              onChange={setActiveScope}
              options={scopeOptions}
              placeholder={t("Select scope")}
              searchPlaceholder={t("Search scope")}
              emptyText={t("No scope found")}
            />
          </div>
          <Button variant="outline" onClick={handleSeedDemo}>
            <Sparkles className="mr-2 h-4 w-4" />
            {t("Seed Demo Data")}
          </Button>
          <Button
            variant="outline"
            className="text-destructive"
            onClick={() => setResetOpen(true)}
            disabled={resettingTargets}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {t("Reset All")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={activeScope !== "carbon" && !hasChangedTargets}
          >
            <Save className="mr-2 h-4 w-4" />
            {t("Save Targets")}
          </Button>
        </div>
      </div>

      {activeScope === "frameworks" ? <FrameworkTargetsSection /> : null}
      {activeScope === "carbon" ? <CarbonInventoryTarget /> : null}

      <AppDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title={t("Reset all targets?")}
        description={t(
          "This will remove all targets for the current filters. This cannot be undone.",
        )}
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setResetOpen(false)}
            >
              {t("Cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={async () => {
                const ok = await clearAllTargets();
                if (ok) {
                  setResetOpen(false);
                }
              }}
              disabled={resettingTargets}
            >
              {t("Reset")}
            </Button>
          </>
        }
      />
    </div>
  );
});

export default TargetSettingsPage;
